import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Chip,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

// icons
import { IconLogout, IconSettings } from "@tabler/icons";

// redux & hooks
import { useDispatch, useSelector } from "react-redux";
import { useGQL } from "views/profile/hooks/useGQL";
import { setLoginState } from "store/slices/auth";
import { clearUser } from "store/slices/user";
import { RootState } from "store";

// fallback image
import User1 from "assets/images/users/user-round.svg";

// config
import useConfig from "hooks/useConfig";

const ProfileSection = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { borderRadius } = useConfig();

  const anchorRef = useRef<any>(null);

  const [profileUrl, setProfileUrl] = useState("");
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);

  const { GET_ADMIN_PROFILE } = useGQL();
  const { data, refetch } = GET_ADMIN_PROFILE();
  const { user } = useSelector((state: RootState) => state.user);

  useEffect(() => {
    if (data?.me) {
      setProfileUrl(data?.me?.profileImageUrl);
    }
  }, [data]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("isBrowserVerified");

    dispatch(setLoginState({ isLoggedIn: false, isBrowserVerified: false }));
    dispatch(clearUser());
    navigate("/login", { replace: true });
  };

  const isAccountSettingsPage = location.pathname === "/admin/profile";

  return (
    <>
      <Chip
        sx={{
          height: "48px",
          alignItems: "center",
          borderRadius: "27px",
          transition: "all .2s ease-in-out",
          borderColor:
            theme.palette.mode === "dark"
              ? theme.palette.dark.main
              : theme.palette.primary.light,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.dark.main
              : theme.palette.primary.light,
          "&:hover": {
            borderColor: theme.palette.primary.main,
            background: `${theme.palette.primary.main}!important`,
            color: theme.palette.primary.light,
            "& svg": {
              stroke: theme.palette.primary.light,
            },
          },
          "& .MuiChip-label": {
            lineHeight: 0,
          },
        }}
        icon={
          <Avatar
            src={profileUrl || User1}
            sx={{
              ...theme.typography.mediumAvatar,
              margin: "8px 0 8px 8px !important",
              cursor: "pointer",
            }}
            ref={anchorRef}
          />
        }
        label={
          <IconSettings
            stroke={1.5}
            size="1.5rem"
            color={theme.palette.primary.main}
          />
        }
        variant="outlined"
        onClick={handleMenuOpen}
        color="primary"
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            minWidth: 240,
            paddingY: 1,
            borderRadius: `${borderRadius + 4}px`,
            boxShadow: theme.shadows[6],
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate("/admin/profile");
          }}
          selected={isAccountSettingsPage}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: `${borderRadius + 2}px`,
            paddingY: 1.2,
            paddingX: 2,
            color: "black",
            backgroundColor: isAccountSettingsPage
              ? `${theme.palette.primary.main}!important`
              : "transparent",

            "&:hover": {
              backgroundColor: isAccountSettingsPage
                ? theme.palette.primary.main
                : `${theme.palette.primary.light}40`,
            },
          }}
        >
          <ListItemIcon sx={{ color: "black", minWidth: 36 }}>
            <IconSettings stroke={1.5} size="1.3rem" />
          </ListItemIcon>
          <ListItemText>
            <Typography
              variant="body2"
              sx={{ color: "black", fontWeight: 500 }}
            >
              Account Settings
            </Typography>
          </ListItemText>
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            handleLogout();
          }}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: `${borderRadius + 2}px`,
            paddingY: 1.2,
            paddingX: 2,
            color: "black",
            "&:hover": {
              backgroundColor: `${theme.palette.primary.light}40`,
            },
          }}
        >
          <ListItemIcon sx={{ color: "black", minWidth: 36 }}>
            <IconLogout stroke={1.5} size="1.3rem" />
          </ListItemIcon>
          <ListItemText>
            <Typography
              variant="body2"
              sx={{ color: "black", fontWeight: 500 }}
            >
              Logout
            </Typography>
          </ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default ProfileSection;
